import { useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import team from "../asserts/about.png";
import fox from "../asserts/Fox alone.png";
import {
  IconArrowUpRight,
  IconSchool,
  IconCode,
  IconSpeakerphone,
  IconMapPin,
  IconCheck,
  IconUsers,
  IconTarget,
  IconBriefcase,
  IconRocket,
  IconBuildingStore,
  IconWorld,
} from "@tabler/icons-react";

import Footer from "../components/Footer";
import LeadershipSection from "../components/directors";

/* =========================================================
   ANIMATIONS
========================================================= */

const fadeUp = {
  hidden: {
    opacity: 0,
    y: 35,
  },

  visible: {
    opacity: 1,
    y: 0,

    transition: {
      duration: 0.7,
      ease: [0.22, 1, 0.36, 1],
    },
  },
};

const fadeLeft = {
  hidden: {
    opacity: 0,
    x: -50,
  },

  visible: {
    opacity: 1,
    x: 0,

    transition: {
      duration: 0.8,
      ease: [0.22, 1, 0.36, 1],
    },
  },
};

const fadeRight = {
  hidden: {
    opacity: 0,
    x: 50,
  },

  visible: {
    opacity: 1,
    x: 0,

    transition: {
      duration: 0.8,
      ease: [0.22, 1, 0.36, 1],
    },
  },
};

const stagger = {
  hidden: {},

  visible: {
    transition: {
      staggerChildren: 0.1,
    },
  },
};

const cardAnimation = {
  hidden: {
    opacity: 0,
    y: 35,
    scale: 0.97,
  },

  visible: {
    opacity: 1,
    y: 0,
    scale: 1,

    transition: {
      duration: 0.65,
      ease: [0.22, 1, 0.36, 1],
    },
  },
};

/* =========================================================
   ABOUT PAGE
========================================================= */

export default function About() {
  useEffect(() => {
    document.title =
      "About WE3X | IT Training, Software & Digital Marketing in Karaikudi";

    let description = document.querySelector('meta[name="description"]');

    if (!description) {
      description = document.createElement("meta");
      description.name = "description";
      document.head.appendChild(description);
    }

    description.content =
      "Learn about WE3X, a Karaikudi-based technology and digital growth company offering IT training, custom software development, web and app development, SEO and digital marketing services.";
  }, []);

  return (
    <main className="overflow-hidden bg-white text-neutral-950">
      {/* =====================================================
          1. ABOUT HERO
      ===================================================== */}

      <section
        className="
          relative
          overflow-hidden
          border-b
          border-neutral-200
          bg-white
          pt-32
          pb-20
          sm:pt-36
          sm:pb-24
          lg:pt-40
          lg:pb-28
        "
      >
        {/* Background decoration */}

        <div
          className="
            pointer-events-none
            absolute
            -right-40
            top-10
            h-[500px]
            w-[500px]
            rounded-full
            bg-blue-100/50
            blur-[120px]
          "
        />

        <div className="relative mx-auto max-w-7xl px-5 sm:px-8 lg:px-10">
          <div
            className="
              grid
              items-center
              gap-14
              lg:grid-cols-[1.08fr_0.92fr]
              lg:gap-20
            "
          >
            {/* LEFT */}

            <motion.div variants={fadeLeft} initial="hidden" animate="visible">
              <div
                className="
                  mb-6
                  inline-flex
                  items-center
                  gap-2
                  rounded-full
                  border
                  border-blue-200
                  bg-blue-50
                  px-4
                  py-2
                  text-xs
                  font-bold
                  uppercase
                  tracking-[0.16em]
                  text-[#55aef3]
                "
              >
                <span className="h-2 w-2 rounded-full bg-black" />
                About WE3X
              </div>

              <h1
                className="
                  merriweather
                  max-w-4xl
                  text-4xl
                  font-black
                  tracking-[-0.045em]
                  text-neutral-950
                  sm:text-5xl
                  lg:text-[64px]
                  lg:leading-[1.05]
                "
              >
                Building skills.
                <br />
                Building technology.
                <span className="text-[#55aef3]"> Building growth.</span>
              </h1>

              <p
                className="
                  mt-7
                  max-w-2xl
                  text-[16px]
                  leading-8
                  text-neutral-600
                  sm:text-[17px]
                "
              >
                WE3X is a technology and digital growth company based in
                Karaikudi, Tamil Nadu, bringing together industry-focused IT
                training, custom software development and performance-driven
                digital marketing under one brand.
              </p>

              <p
                className="
                  mt-4
                  max-w-2xl
                  text-[16px]
                  leading-8
                  text-neutral-600
                "
              >
                We support students building their careers and businesses
                looking to create digital products, improve online visibility
                and automate their operations.
              </p>

              <div className="mt-9 flex flex-col gap-3 sm:flex-row">
                <Link
                  to="/institute"
                  className="
                    group
                    inline-flex
                    items-center
                    justify-center
                    gap-2
                    rounded-full
                    bg-black
                    px-7
                    py-3.5
                    text-sm
                    font-bold
                    text-white
                    transition-all
                    duration-300
                    hover:-translate-y-1
                    hover:bg-blue-700
                    hover:shadow-xl
                    hover:shadow-blue-600/20
                  "
                >
                  Explore WE3X Institute
                  <IconArrowUpRight
                    size={17}
                    className="
                      transition-transform
                      group-hover:translate-x-1
                      group-hover:-translate-y-1
                    "
                  />
                </Link>

                <Link
                  to="/technology"
                  className="
                    group
                    inline-flex
                    items-center
                    justify-center
                    gap-2
                    rounded-full
                    border
                    border-neutral-300
                    bg-white
                    px-7
                    py-3.5
                    text-sm
                    font-bold
                    text-neutral-950
                    transition-all
                    duration-300
                    hover:-translate-y-1
                    hover:border-neutral-950
                  "
                >
                  Explore WE3X Technologies
                  <IconArrowUpRight size={17} />
                </Link>
              </div>
            </motion.div>

            {/* RIGHT — BRAND ECOSYSTEM */}

            <motion.div
              variants={fadeRight}
              initial="hidden"
              animate="visible"
              className="relative"
            >
              <div
                className="
                  relative
                  overflow-hidden
                  rounded-[32px]
                  border
                  border-neutral-200
                  bg-[#071426]
                  p-7
                  text-white
                  shadow-2xl
                  shadow-black/10
                  sm:p-9
                  lg:p-10
                "
              >
                <div
                  className="
                    pointer-events-none
                    absolute
                    -right-20
                    -top-20
                    h-60
                    w-60
                    rounded-full
                    bg-black/30
                    blur-[80px]
                  "
                />

                <p
                  className="
                    relative
                    text-[11px]
                    font-bold
                    uppercase
                    tracking-[0.2em]
                    text-[#55aef3]
                  "
                >
                  One Brand. Two Journeys.
                </p>

                <h2
                  className="
                    merriweather
                    relative
                    mt-4
                    max-w-md
                    text-3xl
                    font-black
                    leading-tight
                    sm:text-4xl
                  "
                >
                  Education for people.
                  <br />
                  Technology for business.
                </h2>

                <div className="relative mt-9 space-y-4">
                  <JourneyItem
                    icon={<IconSchool size={22} />}
                    title="WE3X Institute"
                    text="Courses, practical training, internships, certification and career support for students and job seekers."
                  />

                  <JourneyItem
                    icon={<IconCode size={22} />}
                    title="WE3X Technologies"
                    text="Software, websites, apps, POS, CRM, automation and digital solutions for businesses and founders."
                  />

                  <JourneyItem
                    icon={<IconSpeakerphone size={22} />}
                    title="Digital Growth"
                    text="SEO, Google Ads, social media marketing, branding and lead generation for growing brands."
                  />
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      <section className="bg-white py-20 sm:py-24 lg:py-28">
        <div className="mx-auto max-w-7xl px-5 sm:px-8 lg:px-10">
          <div
            className="
              grid
              items-center
              gap-12
              lg:grid-cols-[0.95fr_1.05fr]
              lg:gap-20
            "
          >
            {/* IMAGE */}

            {/* IMAGE */}

            <motion.div
              variants={fadeLeft}
              initial="hidden"
              whileInView="visible"
              viewport={{
                once: true,
                amount: 0.2,
              }}
              className="
    flex
    items-center
    justify-center
    lg:justify-start
  "
            >
              <div
                className="
      relative
      aspect-[1400/1024]
      w-full
      max-w-[600px]
    "
              >
                <motion.img
                  src={team}
                  alt="WE3X team representing IT training, software development, business automation and digital marketing"
                  className="
        absolute
        inset-0
        h-full
        w-full
        object-contain
      "
                  initial={{
                    opacity: 0,
                    scale: 0.96,
                    y: 20,
                  }}
                  whileInView={{
                    opacity: 1,
                    scale: 1,
                    y: 0,
                  }}
                  viewport={{
                    once: true,
                  }}
                  transition={{
                    duration: 1,
                    ease: [0.22, 1, 0.36, 1],
                  }}
                />
              </div>
            </motion.div>

            {/* CONTENT */}

            <motion.div
              variants={fadeRight}
              initial="hidden"
              whileInView="visible"
              viewport={{
                once: true,
                amount: 0.25,
              }}
            >
              <SectionLabel>Who We Are</SectionLabel>

              <h2
                className="
                  merriweather
                  mt-5
                  max-w-2xl
                  text-3xl
                  font-black
                  tracking-[-0.04em]
                  sm:text-4xl
                  lg:text-[48px]
                  lg:leading-[1.1]
                "
              >
                Technology and learning designed for real-world results.
              </h2>

              <p className="mt-6 max-w-2xl text-[16px] leading-8 text-neutral-600">
                WE3X combines professional IT education with technology services
                and digital marketing. Our goal is to make learning practical
                for students while helping businesses adopt technology that
                supports everyday operations and long-term growth.
              </p>

              <p className="mt-4 max-w-2xl text-[16px] leading-8 text-neutral-600">
                Through WE3X Institute, learners develop job-focused skills
                through practical training. Through WE3X Technologies,
                businesses can access custom software, websites, mobile
                applications, business automation, SEO and digital marketing
                solutions.
              </p>

              <div className="mt-8 grid gap-4 sm:grid-cols-2">
                <CheckItem text="Practical, industry-focused learning" />

                <CheckItem text="Custom software development" />

                <CheckItem text="Business automation solutions" />

                <CheckItem text="Performance-driven digital marketing" />
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      <LeadershipSection />
      {/* =====================================================
          3. TWO CLEAR JOURNEYS
      ===================================================== */}

      <section className="bg-neutral-50 py-20 sm:py-24 lg:py-28">
        <div className="mx-auto max-w-7xl px-5 sm:px-8 lg:px-10">
          <motion.div
            variants={fadeUp}
            initial="hidden"
            whileInView="visible"
            viewport={{
              once: true,
            }}
            className="mx-auto mb-14 max-w-3xl text-center"
          >
            <SectionLabel center>Choose Your Path</SectionLabel>

            <h2
              className="
                merriweather
                mt-5
                text-3xl
                font-black
                tracking-[-0.04em]
                sm:text-4xl
                lg:text-[48px]
                flex
                flex-col
                
              "
            >
              <span>One WE3X.</span>
              <br />
              <span className="text-[#55aef3]">Two focused experiences.</span>
            </h2>

            <p className="mx-auto mt-5 max-w-2xl text-[16px] leading-7 text-neutral-600">
              Students and businesses have different goals. WE3X keeps both
              journeys clear so each audience can find the training, technology
              or growth solution they need.
            </p>
          </motion.div>

          <motion.div
            variants={stagger}
            initial="hidden"
            whileInView="visible"
            viewport={{
              once: true,
              amount: 0.15,
            }}
            className="grid gap-6 lg:grid-cols-2"
          >
            {/* INSTITUTE */}

            <PathCard
              icon={<IconSchool size={27} />}
              number="01"
              title="WE3X Institute"
              audience="For students, graduates & job seekers"
              description="Build practical, job-focused skills through training, projects, internships, certification and career support."
              items={[
                "MERN Stack Development",
                "Digital Marketing",
                "UI/UX & Graphic Design",
                "Video Editing",
                "Cyber Security",
                "Tally Prime",
              ]}
              link="/institute"
              linkText="Explore WE3X Institute"
            />

            {/* TECHNOLOGY */}

            <PathCard
              icon={<IconCode size={27} />}
              number="02"
              title="WE3X Technologies"
              audience="For businesses, founders & companies"
              description="Build digital products, automate operations and grow online through technology and digital marketing services."
              items={[
                "Custom Software",
                "Web Development",
                "Mobile Applications",
                "Billing & POS",
                "CRM & Automation",
                "SEO & Digital Marketing",
              ]}
              link="/technology"
              linkText="Explore WE3X Technologies"
              dark
            />
          </motion.div>
        </div>
      </section>

      {/* =====================================================
          4. WHY CHOOSE WE3X
      ===================================================== */}

      <section className="bg-white py-20 sm:py-24 lg:py-28">
        <div className="mx-auto max-w-7xl px-5 sm:px-8 lg:px-10">
          <motion.div
            variants={fadeUp}
            initial="hidden"
            whileInView="visible"
            viewport={{
              once: true,
            }}
            className="mb-14 max-w-3xl"
          >
            <SectionLabel>Why WE3X</SectionLabel>

            <h2
              className="
                merriweather
                mt-5
                text-3xl
                font-black
                tracking-[-0.04em]
                sm:text-4xl
                lg:text-[48px]
                flex flex-col
              "
            >
              <span>Practical learning.</span>
              <br />
              <span>Real business solutions.</span>
            </h2>
          </motion.div>

          <motion.div
            variants={stagger}
            initial="hidden"
            whileInView="visible"
            viewport={{
              once: true,
              amount: 0.15,
            }}
            className="grid gap-5 sm:grid-cols-2 lg:grid-cols-4"
          >
            <ReasonCard
              icon={<IconTarget size={24} />}
              number="01"
              title="Industry-Focused Learning"
              text="Practical skills aligned with current industry requirements."
            />

            <ReasonCard
              icon={<IconBriefcase size={24} />}
              number="02"
              title="Real-World Projects"
              text="Learn and build through practical projects and real business use cases."
            />

            <ReasonCard
              icon={<IconUsers size={24} />}
              number="03"
              title="Expert Guidance"
              text="Learn and work with professionals across technology and digital marketing."
            />

            <ReasonCard
              icon={<IconRocket size={24} />}
              number="04"
              title="Career & Business Growth"
              text="Build your career through training or grow your business through technology and digital marketing."
            />
          </motion.div>
        </div>
      </section>

      {/* =====================================================
          5. WHO WE SERVE
      ===================================================== */}

      <section className="bg-[#071426] py-20 text-white sm:py-24 lg:py-28">
        <div className="mx-auto max-w-7xl px-5 sm:px-8 lg:px-10">
          <div
            className="
              grid
              gap-14
              lg:grid-cols-[0.75fr_1.25fr]
              lg:gap-20
            "
          >
            {/* LEFT */}

            <motion.div
              variants={fadeLeft}
              initial="hidden"
              whileInView="visible"
              viewport={{
                once: true,
              }}
            >
              <div className="text-xs font-bold uppercase tracking-[0.2em] text-[#55aef3]">
                Who We Serve
              </div>

              <h2
                className="
                  merriweather
                  mt-5
                  text-3xl
                  font-black
                  tracking-[-0.04em]
                  sm:text-4xl
                  lg:text-[48px]
                  lg:leading-[1.1]
                "
              >
                Digital solutions for every stage of business.
              </h2>

              <p className="mt-6 max-w-lg text-[15px] leading-7 text-white/60">
                WE3X supports students, entrepreneurs and businesses with
                practical training, digital products and growth-focused
                technology services.
              </p>
            </motion.div>

            {/* RIGHT */}

            <motion.div
              variants={stagger}
              initial="hidden"
              whileInView="visible"
              viewport={{
                once: true,
                amount: 0.1,
              }}
              className="grid gap-3"
            >
              <AudienceCard
                icon={<IconRocket size={20} />}
                title="Startups"
                text="Build your website, brand identity and digital presence."
              />

              <AudienceCard
                icon={<IconBuildingStore size={20} />}
                title="Retail Businesses"
                text="Manage billing, inventory, customers and business operations."
              />

              <AudienceCard
                icon={<IconSchool size={20} />}
                title="Educational Institutions"
                text="Build websites, generate leads and manage digital campaigns."
              />

              <AudienceCard
                icon={<IconBriefcase size={20} />}
                title="Small & Medium Businesses"
                text="Improve visibility, automate operations and generate leads."
              />

              <AudienceCard
                icon={<IconWorld size={20} />}
                title="Growing Enterprises"
                text="Develop scalable software and performance marketing systems."
              />
            </motion.div>
          </div>
        </div>
      </section>
      <section className="relative overflow-hidden bg-neutral-100 py-20 sm:py-24 lg:py-28">
        <div className="mx-auto max-w-7xl px-5 sm:px-8 lg:px-10">
          <div
            className="
        grid
        items-center
        gap-12
        lg:grid-cols-[0.9fr_1.1fr]
        lg:gap-20
      "
          >
            <motion.div
              variants={fadeLeft}
              initial="hidden"
              whileInView="visible"
              viewport={{
                once: true,
                amount: 0.25,
              }}
              className="
          relative
          flex
          items-center
          justify-center
          lg:justify-start
        "
            >
              {/* IMAGE */}
              <motion.img
                src={fox}
                alt="WE3X Technologies team working on software development and digital marketing projects in Karaikudi"
                className="
            relative
            z-10
            h-auto
            max-h-[340px]
            w-auto
            max-w-full
            object-contain
            sm:max-h-[400px]
            lg:max-h-[460px]
          "
                initial={{
                  opacity: 0,
                  x: -40,
                  scale: 0.96,
                }}
                whileInView={{
                  opacity: 1,
                  x: 0,
                  scale: 1,
                }}
                viewport={{
                  once: true,
                }}
                transition={{
                  duration: 0.9,
                  ease: [0.22, 1, 0.36, 1],
                }}
              />
            </motion.div>

            {/* =====================================================
          RIGHT SIDE CONTENT
      ===================================================== */}

            <motion.div
              variants={fadeRight}
              initial="hidden"
              whileInView="visible"
              viewport={{
                once: true,
                amount: 0.25,
              }}
              className="max-w-2xl"
            >
              {/* LABEL */}
              <motion.div
                variants={fadeUp}
                className="
            mb-6
            flex
            items-center
            gap-3
            text-xs
            font-bold
            uppercase
            tracking-[0.2em]
            text-blue-600
          "
              >
                <span
                  className="
              flex
              h-10
              w-10
              items-center
              justify-center
              rounded-full
              text-black
            "
                >
                  <IconMapPin size={25} />
                </span>
                Our Location
              </motion.div>

              {/* HEADING */}
              <motion.h2
                variants={fadeUp}
                className="
            merriweather
            max-w-2xl
            text-3xl
            font-black
            tracking-[-0.04em]
            text-neutral-950
            sm:text-4xl
            lg:text-[52px]
            lg:leading-[1.08]
          "
              >
                WE3X in{" "}
                <span className="text-[#55aef3]">Karaikudi, Tamil Nadu</span>
              </motion.h2>

              {/* CONTENT */}
              <motion.p
                variants={fadeUp}
                className="
            mt-6
            max-w-xl
            text-[16px]
            leading-8
            text-neutral-600
          "
              >
                Located in Karaikudi, Tamil Nadu, WE3X brings together
                professional IT education, software development and digital
                marketing under one technology-driven brand.
              </motion.p>

              <motion.p
                variants={fadeUp}
                className="
            mt-4
            max-w-xl
            text-[16px]
            leading-8
            text-neutral-600
          "
              >
                Our institute provides practical, industry-focused training,
                while WE3X Technologies helps businesses build digital products,
                improve online visibility and automate their operations.
              </motion.p>

              <motion.p
                variants={fadeUp}
                className="
            mt-4
            max-w-xl
            text-[16px]
            font-medium
            leading-8
            text-neutral-800
          "
              >
                Serving students, startups, small businesses and growing
                companies in Karaikudi and across Tamil Nadu.
              </motion.p>

              {/* CTA */}
              <motion.div variants={fadeUp} className="mt-8">
                <Link
                  to="/contact"
                  className="
              group
              inline-flex
              items-center
              justify-center
              gap-2
              rounded-full
              bg-black
              px-6
              py-3.5
              text-sm
              font-bold
              text-white
              transition-all
              duration-300
              hover:-translate-y-1
              hover:bg-blue-600
            "
                >
                  Contact WE3X in Karaikudi
                  <IconArrowUpRight
                    size={17}
                    className="
                transition-transform
                duration-300
                group-hover:translate-x-1
                group-hover:-translate-y-1
              "
                  />
                </Link>
              </motion.div>
            </motion.div>
          </div>
        </div>
      </section>
      {/* =====================================================
          7. FINAL CTA
      ===================================================== */}

      <section className="bg-neutral-50 py-20 sm:py-24">
        <motion.div
          variants={fadeUp}
          initial="hidden"
          whileInView="visible"
          viewport={{
            once: true,
          }}
          className="mx-auto max-w-4xl px-5 text-center sm:px-8"
        >
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#55aef3]">
            Your Next Step
          </p>

          <h2
            className="
              merriweather
              mt-5
              text-3xl
              font-black
              tracking-[-0.04em]
              sm:text-4xl
              lg:text-[50px]
            "
          >
            Ready to build your next step?
          </h2>

          <p className="mx-auto mt-5 max-w-2xl text-[16px] leading-7 text-neutral-600">
            Start learning. Build your technology. Grow your business with WE3X.
          </p>

          <div className="mt-8 flex flex-col justify-center gap-3 sm:flex-row">
            <Link
              to="/institute"
              className="
                inline-flex
                items-center
                justify-center
                gap-2
                rounded-full
                bg-black
                px-7
                py-3.5
                text-sm
                font-bold
                text-white
                transition-all
                hover:-translate-y-1
                hover:bg-blue-700
              "
            >
              Start Learning
              <IconArrowUpRight size={17} />
            </Link>

            <Link
              to="/contact"
              className="
                inline-flex
                items-center
                justify-center
                gap-2
                rounded-full
                border
                border-neutral-300
                bg-white
                px-7
                py-3.5
                text-sm
                font-bold
                text-neutral-950
                transition-all
                hover:-translate-y-1
                hover:border-neutral-950
              "
            >
              Discuss Your Project
              <IconArrowUpRight size={17} />
            </Link>
          </div>
        </motion.div>
      </section>

      <Footer />
    </main>
  );
}

/* =========================================================
   SECTION LABEL
========================================================= */

function SectionLabel({ children, center = false }) {
  return (
    <div
      className={`
        flex
        items-center
        gap-3
        text-xs
        font-bold
        uppercase
        tracking-[0.2em]
        text-neutral-500
        ${center ? "justify-center" : ""}
      `}
    >
      <span className="h-[1px] w-8 bg-blue-500" />

      {children}
    </div>
  );
}

/* =========================================================
   JOURNEY ITEM
========================================================= */

function JourneyItem({ icon, title, text }) {
  return (
    <div
      className="
        flex
        gap-4
        rounded-2xl
        border
        border-white/10
        bg-white/[0.05]
        p-5
        backdrop-blur-sm
      "
    >
      <div
        className="
          flex
          h-11
          w-11
          shrink-0
          items-center
          justify-center
          rounded-xl
          bg-black
          text-white
        "
      >
        {icon}
      </div>

      <div>
        <h3 className="text-sm font-bold text-white">{title}</h3>

        <p className="mt-1.5 text-xs leading-5 text-white/55">{text}</p>
      </div>
    </div>
  );
}

/* =========================================================
   CHECK ITEM
========================================================= */

function CheckItem({ text }) {
  return (
    <div className="flex items-center gap-3">
      <div
        className="
          flex
          h-7
          w-7
          shrink-0
          items-center
          justify-center
          rounded-full
          bg-blue-50
          text-[#55aef3]
        "
      >
        <IconCheck size={15} stroke={2.3} />
      </div>

      <span className="text-sm font-semibold text-neutral-700">{text}</span>
    </div>
  );
}

/* =========================================================
   PATH CARD
========================================================= */

function PathCard({
  icon,
  number,
  title,
  audience,
  description,
  items,
  link,
  linkText,
  dark = false,
}) {
  return (
    <motion.article
      variants={cardAnimation}
      whileHover={{
        y: -6,
      }}
      className={`
        group
        relative
        overflow-hidden
        rounded-[28px]
        border
        p-7
        sm:p-9
        lg:p-10
        ${
          dark
            ? "border-[#071426] bg-[#071426] text-white"
            : "border-neutral-200 bg-white text-neutral-950"
        }
      `}
    >
      <div className="flex items-start justify-between">
        <div
          className={`
            flex
            h-13
            w-13
            items-center
            justify-center
            rounded-xl
            ${dark ? "bg-black text-white" : "bg-blue-50 text-[#55aef3]"}
          `}
        >
          {icon}
        </div>

        <span
          className={`
            merriweather
            text-4xl
            font-bold
            ${dark ? "text-white/10" : "text-neutral-100"}
          `}
        >
          {number}
        </span>
      </div>

      <p
        className={`
          mt-8
          text-xs
          font-bold
          uppercase
          tracking-[0.14em]
          ${dark ? "text-[#55aef3]" : "text-[#55aef3]"}
        `}
      >
        {audience}
      </p>

      <h3
        className="
          merriweather
          mt-3
          text-3xl
          font-black
          tracking-[-0.03em]
        "
      >
        {title}
      </h3>

      <p
        className={`
          mt-4
          max-w-lg
          text-sm
          leading-7
          ${dark ? "text-white/55" : "text-neutral-600"}
        `}
      >
        {description}
      </p>

      <div className="mt-7 grid gap-2 sm:grid-cols-2">
        {items.map((item) => (
          <div
            key={item}
            className={`
              flex
              items-center
              gap-2
              rounded-lg
              px-3
              py-2.5
              text-xs
              font-semibold
              ${
                dark
                  ? "bg-white/[0.06] text-white/70"
                  : "bg-neutral-50 text-neutral-700"
              }
            `}
          >
            <span
              className={`
                h-1.5
                w-1.5
                rounded-full
                ${dark ? "bg-blue-400" : "bg-black"}
              `}
            />

            {item}
          </div>
        ))}
      </div>

      <Link
        to={link}
        className={`
          mt-8
          inline-flex
          items-center
          gap-2
          text-sm
          font-bold
          ${dark ? "text-[#55aef3]" : "text-neutral-950"}
        `}
      >
        {linkText}

        <IconArrowUpRight
          size={17}
          className="
            transition-transform
            group-hover:translate-x-1
            group-hover:-translate-y-1
          "
        />
      </Link>
    </motion.article>
  );
}

/* =========================================================
   WHY CHOOSE CARD
========================================================= */

function ReasonCard({ icon, number, title, text }) {
  return (
    <motion.article
      variants={cardAnimation}
      whileHover={{
        y: -5,
      }}
      className="
        group
        min-h-[260px]
        rounded-[22px]
        border
        border-neutral-200
        bg-white
        p-6
        transition-shadow
        hover:shadow-xl
        hover:shadow-black/[0.04]
      "
    >
      <div className="flex items-center justify-between">
        <div
          className="
            flex
            h-11
            w-11
            items-center
            justify-center
            rounded-xl
            bg-blue-50
            text-[#55aef3]
          "
        >
          {icon}
        </div>

        <span className="merriweather text-3xl text-neutral-200">{number}</span>
      </div>

      <h3 className="merriweather mt-8 text-xl font-black">{title}</h3>

      <p className="mt-3 text-sm leading-6 text-neutral-600">{text}</p>
    </motion.article>
  );
}

/* =========================================================
   AUDIENCE CARD
========================================================= */

function AudienceCard({ icon, title, text }) {
  return (
    <motion.div
      variants={cardAnimation}
      className="
        group
        flex
        items-center
        gap-5
        rounded-2xl
        border
        border-white/10
        bg-white/[0.04]
        p-5
        transition
        duration-300
        hover:border-blue-500/40
        hover:bg-white/[0.07]
      "
    >
      <div
        className="
          flex
          h-11
          w-11
          shrink-0
          items-center
          justify-center
          rounded-xl
          bg-black/15
          text-[#55aef3]
        "
      >
        {icon}
      </div>

      <div>
        <h3 className="text-sm font-bold text-white">{title}</h3>

        <p className="mt-1 text-xs leading-5 text-white/50">{text}</p>
      </div>
    </motion.div>
  );
}
